import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, MoreHorizontal, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { PageHeader } from "@/client/components/page-header";
import { PropagationBadge } from "@/client/components/propagation-badge";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/client/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/client/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { Skeleton } from "@/client/components/ui/skeleton";
import {
  type AdminPendingInvite,
  type AdminTenant,
  type AdminTenantAllowedIp,
  type AdminTenantFeature,
  type AdminTenantMember,
  type AdminTenantNode,
  type AdminTenantSshKey,
  type DeallocationTarget,
  tenantsAdminApi,
} from "@/client/features/admin/tenants-admin-api";
import { TenantStatusBadge } from "@/client/features/admin/tenant-status-badge";
import { useAuthStore } from "@/client/features/auth/auth-store";
import {
  ALL_RACKS,
  type RackOption,
  rackKeyOf,
  rackOptionLabel,
  rackOptionsFrom,
} from "@/client/features/nodes/rack-filter";
import type { Hold } from "@/client/features/maintenance/holds-api";
import { ApiError } from "@/client/lib/api";
import { formatDateTime } from "@/client/lib/format";
import type { FulfillmentMode, TenantFeature } from "@/shared/features";
import { countPorts } from "@/shared/ports";
import {
  PROPAGATION_STATUS_LABEL,
  PROPAGATION_STATUSES,
  type PropagationStatus,
} from "@/shared/propagation";
import type { TenantRole } from "@/shared/types";

function inviteRoleLabel(role: TenantRole): string {
  return role === "tenant_admin" ? "Admin" : "Member";
}

function roleLabel(m: AdminTenantMember): string {
  return m.tenantRole === "tenant_admin" ? "Admin" : "Member";
}

function MembersCard({
  members,
  error,
  canResetMfa,
  mfaRequired,
  onReset,
}: {
  members: AdminTenantMember[] | null;
  error: boolean;
  canResetMfa: boolean;
  /** Whether this tenant is armed for MFA (`mfaRequiredAt` set). */
  mfaRequired: boolean;
  onReset: (member: AdminTenantMember) => Promise<void>;
}) {
  const [resetTarget, setResetTarget] = useState<AdminTenantMember | null>(null);
  const [resetting, setResetting] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Members</CardTitle>
        <CardDescription>People in this organization with portal access.</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Only show the error screen when we have nothing else to show: a
            failed refresh (e.g. after an invite) must not wipe a roster we
            already loaded. The toast surfaces the refresh failure. */}
        {error && members === null ? (
          <p className="text-muted-foreground text-sm">
            Couldn't load members. Refresh to try again.
          </p>
        ) : members === null ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : members.length === 0 ? (
          <p className="text-muted-foreground text-sm">No members yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b text-left">
                  <th className="py-2 pr-4 font-medium">Name</th>
                  <th className="py-2 pr-4 font-medium">Email</th>
                  <th className="py-2 pr-4 font-medium">Role</th>
                  <th className="py-2 pr-4 font-medium">MFA</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  {canResetMfa ? <th className="py-2 font-medium">Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">{m.name ?? "—"}</td>
                    <td className="text-muted-foreground py-2 pr-4">{m.email}</td>
                    <td className="py-2 pr-4">
                      <Badge variant={m.tenantRole === "tenant_admin" ? "secondary" : "outline"}>
                        {roleLabel(m)}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4">
                      {/* Only flag missing enrollment as a problem once the
                          tenant is actually armed for MFA; on un-armed tenants a
                          not-yet-enrolled member is expected, not out of compliance. */}
                      <Badge
                        variant={
                          m.mfaEnabled ? "outline" : mfaRequired ? "destructive" : "secondary"
                        }
                      >
                        {m.mfaEnabled ? "Enabled" : mfaRequired ? "Setup required" : "Not enrolled"}
                      </Badge>
                    </td>
                    <td className="py-2">
                      <Badge variant={m.status === "active" ? "outline" : "destructive"}>
                        {m.status}
                      </Badge>
                    </td>
                    {canResetMfa ? (
                      <td className="py-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for ${m.email}`}
                            >
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={!m.mfaEnabled}
                              onSelect={() => setResetTarget(m)}
                            >
                              Reset MFA
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Dialog open={Boolean(resetTarget)} onOpenChange={(open) => !open && setResetTarget(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reset customer MFA?</DialogTitle>
              <DialogDescription>
                This revokes all sessions for {resetTarget?.email} and forces authenticator
                enrollment at their next sign-in. Follow the identity-verification runbook first.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetTarget(null)} disabled={resetting}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={resetting || !resetTarget}
                onClick={() => {
                  if (!resetTarget) return;
                  setResetting(true);
                  void onReset(resetTarget)
                    .then(() => setResetTarget(null))
                    .finally(() => setResetting(false));
                }}
              >
                {resetting ? "Resetting…" : "Reset MFA"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

const inviteSchema = z.object({
  email: z.string().email("Enter a valid email"),
  role: z.enum(["member", "tenant_admin"]),
});

type InviteValues = z.infer<typeof inviteSchema>;

// Staff-admin control to seed a tenant's first admin (the customer team page
// can't, since it requires an existing admin). Defaults to Admin for exactly
// that initial-handoff case.
function InviteCard({ tenantId, onCreated }: { tenantId: string; onCreated: () => void }) {
  const form = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: "", role: "tenant_admin" },
  });

  const onSubmit = async (values: InviteValues) => {
    try {
      await tenantsAdminApi.createInvite(tenantId, values);
      toast.success(`Invitation sent to ${values.email}.`);
      form.reset({ email: "", role: "tenant_admin" });
      onCreated();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Unable to send the invitation. Please try again.",
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite a member</CardTitle>
        <CardDescription>
          Provision a user into this organization - typically the first admin, who can then invite
          the rest of their team. They'll get an email with a link to set up their account.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 sm:flex-row sm:items-end"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="admin@company.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="sm:w-40">
                  <FormLabel>Role</FormLabel>
                  <FormControl>
                    <select
                      className="border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 py-1 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none"
                      {...field}
                    >
                      <option value="tenant_admin">Admin</option>
                      <option value="member">Member</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Sending…" : "Send invite"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

function PendingInvitesCard({
  invites,
  error,
}: {
  invites: AdminPendingInvite[] | null;
  error: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending invites</CardTitle>
        <CardDescription>
          People invited to this organization who haven't joined yet.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {invites && invites.length > 0 ? (
          <ul className="divide-y">
            {invites.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between gap-4 py-2 text-sm">
                <div className="min-w-0">
                  <p className="truncate">{inv.email}</p>
                  <p className="text-muted-foreground text-xs">
                    Expires {new Date(inv.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                <Badge variant={inv.role === "tenant_admin" ? "secondary" : "outline"}>
                  {inviteRoleLabel(inv.role)}
                </Badge>
              </li>
            ))}
          </ul>
        ) : error ? (
          <p className="text-muted-foreground text-sm">
            Couldn't load pending invites. Refresh to try again.
          </p>
        ) : invites === null ? (
          <Skeleton className="h-8 w-full" />
        ) : (
          <p className="text-muted-foreground text-sm">No pending invites.</p>
        )}
      </CardContent>
    </Card>
  );
}

function SshKeysCard({ keys, error }: { keys: AdminTenantSshKey[] | null; error: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>SSH keys</CardTitle>
        <CardDescription>Public keys uploaded by this tenant's members.</CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-muted-foreground text-sm">
            Couldn't load SSH keys. Refresh to try again.
          </p>
        ) : keys === null ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : keys.length === 0 ? (
          <p className="text-muted-foreground text-sm">No SSH keys uploaded.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b text-left">
                  <th className="py-2 pr-4 font-medium">Member</th>
                  <th className="py-2 pr-4 font-medium">Label</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 font-medium">Fingerprint</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-b last:border-0">
                    <td className="text-muted-foreground py-2 pr-4">{k.userEmail}</td>
                    <td className="py-2 pr-4">{k.label ?? "—"}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{k.keyType}</td>
                    <td className="py-2 font-mono text-xs break-all">{k.fingerprint}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AllowedIpsCard({
  entries,
  error,
}: {
  entries: AdminTenantAllowedIp[] | null;
  error: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Network access</CardTitle>
        <CardDescription>
          Source IP ranges this tenant connects from - provision firewall access for these.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-muted-foreground text-sm">
            Couldn't load allowed ranges. Refresh to try again.
          </p>
        ) : entries === null ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-muted-foreground text-sm">No allowed ranges.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b text-left">
                  <th className="py-2 pr-4 font-medium">Range</th>
                  <th className="py-2 pr-4 font-medium">Label</th>
                  <th className="py-2 font-medium">Added by</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-mono text-xs break-all">{e.value}</td>
                    <td className="py-2 pr-4">{e.label ?? "—"}</td>
                    <td className="text-muted-foreground py-2">{e.createdByName ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function nodeStatusVariant(
  status: string | null,
): "secondary" | "outline" | "destructive" | "warning" {
  switch (status) {
    case "production":
    case "active":
      return "secondary";
    case "maintenance":
      return "warning";
    case "decommissioned":
    case "offline":
    case "failed":
      return "destructive";
    default:
      return "outline";
  }
}

const DEALLOCATION_TARGET_LABELS: Record<DeallocationTarget, string> = {
  production: "Production (return to pool)",
  maintenance: "Maintenance",
  decommissioned: "Decommissioned",
};

// Native form controls styled to match the shadcn inputs used elsewhere (the
// InviteCard select), so the node dialogs don't need extra UI primitives.
const selectClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 py-1 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";
const textareaClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 min-h-20 w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

function AssignNodesDialog({
  tenantId,
  open,
  onOpenChange,
  onAllocated,
}: {
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAllocated: (nodes: AdminTenantNode[]) => void;
}) {
  const [pool, setPool] = useState<AdminTenantNode[] | null>(null);
  const [poolError, setPoolError] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [rackFilter, setRackFilter] = useState<string>(ALL_RACKS);
  const [customerVisible, setCustomerVisible] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPool(null);
    setPoolError(false);
    setSelected(new Set());
    setRackFilter(ALL_RACKS);
    setCustomerVisible(false);
    setReason("");
    void tenantsAdminApi
      .availableNodes(tenantId)
      .then((res) => setPool(res.nodes))
      .catch((err) => {
        setPoolError(true);
        toast.error(err instanceof ApiError ? err.message : "Failed to load the available pool.");
      });
  }, [open, tenantId]);

  // Distinct site+rack groups in the pool, naturally sorted by site then rack,
  // with unracked nodes last.
  const rackOptions = useMemo<RackOption[]>(() => rackOptionsFrom(pool ?? []), [pool]);

  // The rows currently visible given the rack filter. Filtering is purely visual;
  // it never changes the existing selection, so staff can stage several racks
  // before allocating.
  const shown = useMemo(() => {
    if (!pool) return [];
    if (rackFilter === ALL_RACKS) return pool;
    return pool.filter((n) => rackKeyOf(n) === rackFilter);
  }, [pool, rackFilter]);

  const shownIds = shown.map((n) => n.netboxDeviceId);
  const allShownSelected = shownIds.length > 0 && shownIds.every((id) => selected.has(id));

  const toggle = (deviceId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  };

  const toggleAllShown = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allShownSelected) for (const id of shownIds) next.delete(id);
      else for (const id of shownIds) next.add(id);
      return next;
    });
  };

  const onSubmit = async () => {
    if (selected.size === 0 || reason.trim().length === 0) return;
    setSubmitting(true);
    try {
      const res = await tenantsAdminApi.allocateNodes(tenantId, {
        deviceIds: [...selected],
        customerVisible,
        reason: reason.trim(),
      });
      onAllocated(res.nodes);
      if (res.failed.length > 0) {
        toast.warning(
          `Allocated ${res.allocated.length}; ${res.failed.length} failed: ${res.failed
            .map((f) => f.error)
            .join("; ")}`,
        );
      } else {
        toast.success(
          `Allocated ${res.allocated.length} node${res.allocated.length === 1 ? "" : "s"}.`,
        );
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to allocate nodes.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign nodes</DialogTitle>
          <DialogDescription>
            Allocate unassigned GPU nodes to this tenant. Allocation assigns the node in NetBox and
            leaves its lifecycle status unchanged - a node in maintenance stays in maintenance.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {pool && pool.length > 0 ? (
            <div className="flex items-center gap-2">
              <select
                aria-label="Filter by rack"
                className={selectClass}
                value={rackFilter}
                onChange={(e) => setRackFilter(e.target.value)}
              >
                <option value={ALL_RACKS}>All racks ({pool.length})</option>
                {rackOptions.map((r) => (
                  <option key={r.key} value={r.key}>
                    {rackOptionLabel(r)} ({r.count})
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="whitespace-nowrap"
                disabled={shown.length === 0}
                onClick={toggleAllShown}
              >
                {allShownSelected ? "Clear shown" : "Select all shown"}
              </Button>
            </div>
          ) : null}
          <div className="max-h-64 overflow-y-auto rounded-md border">
            {poolError ? (
              <p className="text-muted-foreground p-3 text-sm">Couldn't load the available pool.</p>
            ) : pool === null ? (
              <div className="flex flex-col gap-2 p-3">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : pool.length === 0 ? (
              <p className="text-muted-foreground p-3 text-sm">
                No nodes are available to allocate.
              </p>
            ) : shown.length === 0 ? (
              <p className="text-muted-foreground p-3 text-sm">No nodes in this rack.</p>
            ) : (
              <ul className="divide-y">
                {shown.map((n) => (
                  <li key={n.netboxDeviceId}>
                    <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        className="size-4"
                        checked={selected.has(n.netboxDeviceId)}
                        onChange={() => toggle(n.netboxDeviceId)}
                      />
                      <span className="font-mono text-xs break-all">{n.name}</span>
                      {n.status && n.status !== "production" ? (
                        <Badge variant={nodeStatusVariant(n.status)} className="shrink-0">
                          {n.statusLabel ?? n.status}
                        </Badge>
                      ) : null}
                      <span className="text-muted-foreground ml-auto text-xs whitespace-nowrap">
                        {n.rack ? `Rack ${n.rack}` : "Unracked"}
                        {n.gpuType ? ` · ${n.gpuType}` : ""}
                        {n.site ? ` · ${n.site}` : ""}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4"
              checked={customerVisible}
              onChange={(e) => setCustomerVisible(e.target.checked)}
            />
            Make visible to the customer immediately
          </label>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="allocate-reason">Reason</Label>
            <textarea
              id="allocate-reason"
              className={textareaClass}
              placeholder="Why are these nodes being allocated?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void onSubmit()}
            disabled={submitting || selected.size === 0 || reason.trim().length === 0}
          >
            {submitting ? "Allocating…" : `Allocate${selected.size ? ` ${selected.size}` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeallocateDialog({
  tenantId,
  nodes,
  onOpenChange,
  onChanged,
}: {
  tenantId: string;
  // One or many nodes; an empty array keeps the dialog closed.
  nodes: AdminTenantNode[];
  onOpenChange: (open: boolean) => void;
  onChanged: (nodes: AdminTenantNode[]) => void;
}) {
  const [target, setTarget] = useState<DeallocationTarget>("production");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const open = nodes.length > 0;
  const multiple = nodes.length > 1;

  useEffect(() => {
    if (open) {
      setTarget("production");
      setReason("");
    }
  }, [open]);

  const onSubmit = async () => {
    if (nodes.length === 0 || reason.trim().length === 0) return;
    setSubmitting(true);
    const trimmed = reason.trim();
    // No batch endpoint: deallocate each node via the per-device endpoint so the
    // server re-validates the lifecycle transition for each. Aggregate outcomes.
    const failures: string[] = [];
    let latest: AdminTenantNode[] | null = null;
    for (const node of nodes) {
      try {
        const res = await tenantsAdminApi.deallocateNode(tenantId, node.netboxDeviceId, {
          targetStatus: target,
          reason: trimmed,
        });
        latest = res.nodes;
      } catch (err) {
        failures.push(`${node.name}: ${err instanceof ApiError ? err.message : "failed"}`);
      }
    }
    setSubmitting(false);
    if (latest) onChanged(latest);
    const succeeded = nodes.length - failures.length;
    if (failures.length === 0) {
      toast.success(
        multiple ? `Deallocated ${succeeded} nodes.` : `Deallocated ${nodes[0]?.name}.`,
      );
      onOpenChange(false);
    } else {
      toast.warning(`Deallocated ${succeeded}; ${failures.length} failed: ${failures.join("; ")}`);
      // Keep the dialog open only if nothing succeeded, so staff can retry.
      if (succeeded > 0) onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {multiple ? `Deallocate ${nodes.length} nodes` : "Deallocate node"}
          </DialogTitle>
          <DialogDescription>
            {multiple ? (
              <>Move {nodes.length} nodes out of this tenant.</>
            ) : (
              <>
                Move <span className="font-mono text-xs break-all">{nodes[0]?.name}</span> out of
                this tenant.
              </>
            )}{" "}
            Their tenant assignment is cleared and they're hidden from the customer.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="deallocate-target">New state</Label>
            <select
              id="deallocate-target"
              className={selectClass}
              value={target}
              onChange={(e) => setTarget(e.target.value as DeallocationTarget)}
            >
              {(Object.keys(DEALLOCATION_TARGET_LABELS) as DeallocationTarget[]).map((t) => (
                <option key={t} value={t}>
                  {DEALLOCATION_TARGET_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="deallocate-reason">Reason</Label>
            <textarea
              id="deallocate-reason"
              className={textareaClass}
              placeholder="Why is this node being deallocated?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void onSubmit()}
            disabled={submitting || reason.trim().length === 0}
          >
            {submitting ? "Deallocating…" : "Deallocate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Staff manual override of a node's public-IP propagation state (PE-924). The
// early rollout is semi-manual: a customer request lands as `pending`, staff
// provision the address out of band, then verify it here with the assigned IP.
function PublicIpStatusDialog({
  tenantId,
  node,
  onOpenChange,
  onSaved,
}: {
  tenantId: string;
  node: AdminTenantNode | null;
  onOpenChange: (open: boolean) => void;
  onSaved: (deviceId: number, publicIp: AdminTenantNode["publicIp"]) => void;
}) {
  const open = node !== null;
  const [status, setStatus] = useState<PropagationStatus>("pending");
  const [ip, setIp] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (node) {
      setStatus(node.publicIp?.status ?? "pending");
      setIp(node.publicIp?.publicIp ?? "");
    }
  }, [node]);

  const onSubmit = async () => {
    if (!node) return;
    setSubmitting(true);
    try {
      const res = await tenantsAdminApi.setNodePublicIpStatus(tenantId, node.netboxDeviceId, {
        status,
        publicIp: ip.trim() ? ip.trim() : null,
      });
      onSaved(node.netboxDeviceId, res.publicIp);
      toast.success("Public IP status updated.");
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update the public IP status.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Public IP status</DialogTitle>
          <DialogDescription>
            Set the propagation state for{" "}
            <span className="font-mono text-xs break-all">{node?.name}</span>. Verifying records the
            assigned address the customer connects to.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="public-ip-status">State</Label>
            <select
              id="public-ip-status"
              className={selectClass}
              value={status}
              onChange={(e) => setStatus(e.target.value as PropagationStatus)}
            >
              {PROPAGATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {PROPAGATION_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="public-ip-address">
              Assigned public IP{status === "verified" ? "" : " (optional)"}
            </Label>
            <Input
              id="public-ip-address"
              className="font-mono"
              spellCheck={false}
              placeholder="203.0.113.42"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void onSubmit()}
            disabled={submitting || (status === "verified" && ip.trim().length === 0)}
          >
            {submitting ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NodesCard({ tenantId, isStaffAdmin }: { tenantId: string; isStaffAdmin: boolean }) {
  const [nodes, setNodes] = useState<AdminTenantNode[] | null>(null);
  const [netboxConfigured, setNetboxConfigured] = useState(true);
  const [writeEnabled, setWriteEnabled] = useState(false);
  const [mapped, setMapped] = useState(true);
  const [error, setError] = useState(false);
  const [busyDeviceId, setBusyDeviceId] = useState<number | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deallocating, setDeallocating] = useState<AdminTenantNode[]>([]);
  const [publicIpNode, setPublicIpNode] = useState<AdminTenantNode | null>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Patch a single node's public-IP record in place after a staff override (the
  // endpoint returns just the updated record, not the whole node list).
  const onPublicIpSaved = (deviceId: number, publicIp: AdminTenantNode["publicIp"]) =>
    setNodes((prev) =>
      prev ? prev.map((n) => (n.netboxDeviceId === deviceId ? { ...n, publicIp } : n)) : prev,
    );

  const load = useCallback(() => {
    setError(false);
    void tenantsAdminApi
      .nodes(tenantId)
      .then((res) => {
        setNodes(res.nodes);
        setNetboxConfigured(res.netboxConfigured);
        setWriteEnabled(res.writeEnabled);
        setMapped(res.mapped);
      })
      .catch((err) => {
        setError(true);
        toast.error(err instanceof ApiError ? err.message : "Failed to load nodes.");
      });
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const canManage = isStaffAdmin && writeEnabled && mapped;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return nodes ?? [];
    return (nodes ?? []).filter(
      (n) => n.name.toLowerCase().includes(q) || (n.hostname?.toLowerCase().includes(q) ?? false),
    );
  }, [nodes, query]);

  // Drop selections for nodes that disappear after a refresh.
  useEffect(() => {
    if (!nodes) return;
    const ids = new Set(nodes.map((n) => n.netboxDeviceId));
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [nodes]);

  const selectedNodes = (nodes ?? []).filter((n) => selected.has(n.netboxDeviceId));
  const shownIds = filtered.map((n) => n.netboxDeviceId);
  const allShownSelected = shownIds.length > 0 && shownIds.every((id) => selected.has(id));
  const actionsBusy = busyDeviceId !== null || bulkBusy;

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllShown = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allShownSelected) for (const id of shownIds) next.delete(id);
      else for (const id of shownIds) next.add(id);
      return next;
    });

  // Each mutation returns the authoritative full node list, so overlapping
  // mutations could clobber each other (last response wins). busyDeviceId /
  // bulkBusy gate the row + bulk actions while one is in flight, serializing them.
  const onToggleVisibility = async (node: AdminTenantNode) => {
    setBusyDeviceId(node.netboxDeviceId);
    try {
      const res = await tenantsAdminApi.setNodeVisibility(
        tenantId,
        node.netboxDeviceId,
        !node.customerVisible,
      );
      setNodes(res.nodes);
      toast.success(
        node.customerVisible
          ? "Node hidden from the customer."
          : "Node is now visible to the customer.",
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update visibility.");
    } finally {
      setBusyDeviceId(null);
    }
  };

  // No batch endpoint: apply the per-device visibility endpoint to each selected
  // node that needs the change, then summarize. Skips no-ops.
  const onBulkVisibility = async (value: boolean) => {
    const targets = selectedNodes.filter((n) => n.customerVisible !== value);
    if (targets.length === 0) {
      toast.info(
        value ? "Selected nodes are already visible." : "Selected nodes are already hidden.",
      );
      return;
    }
    setBulkBusy(true);
    const failures: string[] = [];
    let latest: AdminTenantNode[] | null = null;
    for (const n of targets) {
      try {
        const res = await tenantsAdminApi.setNodeVisibility(tenantId, n.netboxDeviceId, value);
        latest = res.nodes;
      } catch (err) {
        failures.push(`${n.name}: ${err instanceof ApiError ? err.message : "failed"}`);
      }
    }
    if (latest) setNodes(latest);
    setBulkBusy(false);
    const ok = targets.length - failures.length;
    if (failures.length === 0) {
      toast.success(
        value
          ? `${ok} node${ok === 1 ? "" : "s"} now visible.`
          : `${ok} node${ok === 1 ? "" : "s"} hidden.`,
      );
    } else {
      toast.warning(`Updated ${ok}; ${failures.length} failed: ${failures.join("; ")}`);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle className="flex items-center gap-2">
            Nodes
            {nodes && nodes.length > 0 ? (
              <Badge variant="secondary" className="font-normal">
                {query.trim() && filtered.length !== nodes.length
                  ? `${filtered.length} of ${nodes.length}`
                  : nodes.length}
              </Badge>
            ) : null}
          </CardTitle>
          <CardDescription>
            GPU compute nodes allocated to this tenant in NetBox. Use the row menu to control
            customer visibility or deallocate.
          </CardDescription>
        </div>
        {canManage ? (
          <Button variant="outline" size="sm" onClick={() => setAssignOpen(true)}>
            Assign nodes
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-muted-foreground text-sm">
            Couldn't load nodes. Refresh to try again.
          </p>
        ) : !netboxConfigured ? (
          <p className="text-muted-foreground text-sm">NetBox integration is not configured.</p>
        ) : !mapped ? (
          <p className="text-muted-foreground text-sm">
            This tenant has no NetBox mapping yet, so its nodes can't be shown.
          </p>
        ) : nodes === null ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : nodes.length === 0 ? (
          <p className="text-muted-foreground text-sm">No nodes allocated.</p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative max-w-xs flex-1">
                <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter by name or hostname"
                  className="pl-8"
                  aria-label="Filter nodes"
                />
              </div>
              {canManage ? (
                <div className="bg-muted/60 ml-auto flex flex-wrap items-center gap-2 rounded-md px-2 py-1 text-sm">
                  <span className="text-muted-foreground">
                    {selected.size > 0 ? `${selected.size} selected` : "Select nodes"}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={actionsBusy || selected.size === 0}
                    onClick={() => void onBulkVisibility(true)}
                  >
                    Show
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={actionsBusy || selected.size === 0}
                    onClick={() => void onBulkVisibility(false)}
                  >
                    Hide
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={actionsBusy || selected.size === 0}
                    onClick={() => setDeallocating(selectedNodes)}
                  >
                    Deallocate
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={actionsBusy || selected.size === 0}
                    onClick={() => setSelected(new Set())}
                  >
                    Clear
                  </Button>
                </div>
              ) : null}
            </div>
            {filtered.length === 0 ? (
              <p className="text-muted-foreground text-sm">No nodes match "{query}".</p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr className="text-muted-foreground border-b text-left">
                      {canManage ? (
                        <th className="w-10 py-2 pr-2 pl-3">
                          <input
                            type="checkbox"
                            className="size-4 align-middle"
                            aria-label="Select all shown nodes"
                            checked={allShownSelected}
                            disabled={actionsBusy}
                            onChange={toggleAllShown}
                          />
                        </th>
                      ) : null}
                      <th className="py-2 pr-4 font-medium">Node</th>
                      <th className="py-2 pr-4 font-medium">Hostname</th>
                      <th className="py-2 pr-4 font-medium">GPU type</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Site</th>
                      <th className="py-2 pr-4 font-medium">Rack</th>
                      <th className="py-2 pr-4 font-medium">Serial</th>
                      <th className="py-2 pr-4 font-medium">Visible</th>
                      <th className="py-2 pr-4 font-medium">Public IP</th>
                      {canManage ? <th className="w-10 py-2 pr-3" /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((n) => {
                      const isSelected = selected.has(n.netboxDeviceId);
                      return (
                        <tr
                          key={n.netboxDeviceId}
                          data-selected={isSelected}
                          className="hover:bg-muted/40 data-[selected=true]:bg-muted/60 border-b transition-colors last:border-0"
                        >
                          {canManage ? (
                            <td className="py-2 pr-2 pl-3">
                              <input
                                type="checkbox"
                                className="size-4 align-middle"
                                aria-label={`Select ${n.name}`}
                                checked={isSelected}
                                disabled={actionsBusy}
                                onChange={() => toggle(n.netboxDeviceId)}
                              />
                            </td>
                          ) : null}
                          <td className="py-2 pr-4 font-mono text-xs break-all">{n.name}</td>
                          <td className="text-muted-foreground py-2 pr-4 font-mono text-xs break-all">
                            {n.hostname ?? "—"}
                          </td>
                          <td className="py-2 pr-4">{n.gpuType ?? "—"}</td>
                          <td className="py-2 pr-4">
                            <Badge variant={nodeStatusVariant(n.status)}>
                              {n.statusLabel ?? n.status ?? "—"}
                            </Badge>
                          </td>
                          <td className="text-muted-foreground py-2 pr-4">{n.site ?? "—"}</td>
                          <td className="text-muted-foreground py-2 pr-4">{n.rack ?? "—"}</td>
                          <td className="text-muted-foreground py-2 pr-4 font-mono text-xs break-all">
                            {n.serial ?? "—"}
                          </td>
                          <td className="py-2 pr-4">
                            <Badge variant={n.customerVisible ? "secondary" : "outline"}>
                              {n.customerVisible ? "Visible" : "Hidden"}
                            </Badge>
                          </td>
                          <td className="py-2 pr-4">
                            {n.publicIp ? (
                              <div className="flex flex-col gap-0.5">
                                {n.publicIp.status === "verified" && n.publicIp.publicIp ? (
                                  <span className="font-mono text-xs break-all">
                                    {n.publicIp.publicIp}
                                  </span>
                                ) : (
                                  <PropagationBadge status={n.publicIp.status} className="w-fit" />
                                )}
                                {n.publicIp.ports.length > 0 ? (
                                  <span className="text-muted-foreground text-xs">
                                    {countPorts(n.publicIp.ports)} port
                                    {countPorts(n.publicIp.ports) === 1 ? "" : "s"}
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          {canManage ? (
                            <td className="py-2 pr-3 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="size-8"
                                    disabled={actionsBusy}
                                  >
                                    <MoreHorizontal className="size-4" />
                                    <span className="sr-only">Node actions</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onSelect={() => void onToggleVisibility(n)}>
                                    {n.customerVisible ? "Hide from customer" : "Make visible"}
                                  </DropdownMenuItem>
                                  {n.publicIp ? (
                                    <DropdownMenuItem onSelect={() => setPublicIpNode(n)}>
                                      Set public IP status…
                                    </DropdownMenuItem>
                                  ) : null}
                                  <DropdownMenuItem
                                    variant="destructive"
                                    onSelect={() => setDeallocating([n])}
                                  >
                                    Deallocate…
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
      {canManage ? (
        <>
          <AssignNodesDialog
            tenantId={tenantId}
            open={assignOpen}
            onOpenChange={setAssignOpen}
            onAllocated={setNodes}
          />
          <DeallocateDialog
            tenantId={tenantId}
            nodes={deallocating}
            onOpenChange={(o) => {
              if (!o) setDeallocating([]);
            }}
            onChanged={(n) => {
              setNodes(n);
              setSelected(new Set());
            }}
          />
          <PublicIpStatusDialog
            tenantId={tenantId}
            node={publicIpNode}
            onOpenChange={(o) => {
              if (!o) setPublicIpNode(null);
            }}
            onSaved={onPublicIpSaved}
          />
        </>
      ) : null}
    </Card>
  );
}

// Per-tenant feature entitlements. Staff admins toggle which specialized portal
// capabilities a tenant sees; non-admin staff see the current state read-only.
// Self-contained data load (like NodesCard) so it refreshes independently.
function FeaturesCard({ tenantId, isStaffAdmin }: { tenantId: string; isStaffAdmin: boolean }) {
  const [features, setFeatures] = useState<AdminTenantFeature[] | null>(null);
  const [error, setError] = useState(false);
  const [busyKey, setBusyKey] = useState<TenantFeature | null>(null);

  useEffect(() => {
    let active = true;
    setError(false);
    setFeatures(null);
    void tenantsAdminApi
      .features(tenantId)
      .then((res) => active && setFeatures(res.features))
      .catch((err) => {
        if (active) {
          setError(true);
          toast.error(err instanceof ApiError ? err.message : "Failed to load features.");
        }
      });
    return () => {
      active = false;
    };
  }, [tenantId]);

  const onToggle = async (feature: AdminTenantFeature) => {
    setBusyKey(feature.key);
    try {
      const res = await tenantsAdminApi.setFeature(tenantId, feature.key, !feature.enabled);
      setFeatures(res.features);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update the feature.");
    } finally {
      setBusyKey(null);
    }
  };

  const onSetMode = async (feature: AdminTenantFeature, mode: FulfillmentMode) => {
    if (mode === feature.mode) return;
    setBusyKey(feature.key);
    try {
      const res = await tenantsAdminApi.setFeatureMode(tenantId, feature.key, mode);
      setFeatures(res.features);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update fulfillment mode.");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Features</CardTitle>
        <CardDescription>
          Specialized portal capabilities this tenant can access. Common surfaces (dashboard, team,
          docs, maintenance, support) are always available; the features below are opt-in per
          tenant.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-muted-foreground text-sm">
            Couldn't load features. Refresh to try again.
          </p>
        ) : features === null ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <ul className="divide-y">
            {features.map((f) => (
              <li key={f.key} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{f.label}</p>
                  <p className="text-muted-foreground text-xs">{f.description}</p>
                  {f.modeCapable && f.enabled ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-muted-foreground text-xs">Fulfillment</span>
                      {isStaffAdmin ? (
                        <select
                          className="border-input bg-background h-7 rounded-md border px-2 text-xs"
                          value={f.mode ?? "ticket"}
                          disabled={busyKey !== null}
                          onChange={(e) => void onSetMode(f, e.target.value as FulfillmentMode)}
                          aria-label={`${f.label} fulfillment mode`}
                        >
                          <option value="ticket">Ticket-based</option>
                          <option value="automated">Automated</option>
                        </select>
                      ) : (
                        <Badge variant={f.mode === "automated" ? "warning" : "outline"}>
                          {f.mode === "automated" ? "Automated" : "Ticket-based"}
                        </Badge>
                      )}
                      {f.mode === "automated" ? (
                        <span className="text-xs text-[var(--warning-fg)]">
                          Changes propagate to customer-infra (Terraform).
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {isStaffAdmin ? (
                  <input
                    type="checkbox"
                    className="size-4 shrink-0"
                    checked={f.enabled}
                    disabled={busyKey !== null}
                    onChange={() => void onToggle(f)}
                    aria-label={`${f.enabled ? "Disable" : "Enable"} ${f.label}`}
                  />
                ) : (
                  <Badge variant={f.enabled ? "secondary" : "outline"} className="shrink-0">
                    {f.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function HoldsCard({ holds, error }: { holds: Hold[] | null; error: boolean }) {
  const active = holds?.filter((h) => h.status === "active") ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>No-disruption holds</CardTitle>
        <CardDescription>
          Windows this tenant has asked Mantle not to disrupt their work.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-muted-foreground text-sm">
            Couldn't load holds. Refresh to try again.
          </p>
        ) : holds === null ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : active.length === 0 ? (
          <p className="text-muted-foreground text-sm">No active holds.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b text-left">
                  <th className="py-2 pr-4 font-medium">Title</th>
                  <th className="py-2 pr-4 font-medium">Window</th>
                  <th className="py-2 pr-4 font-medium">Scope</th>
                  <th className="py-2 font-medium">Created by</th>
                </tr>
              </thead>
              <tbody>
                {active.map((h) => (
                  <tr key={h.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-4">{h.title}</td>
                    <td className="text-muted-foreground py-2 pr-4 whitespace-nowrap">
                      {formatDateTime(h.startsAt)} – {formatDateTime(h.endsAt)}
                    </td>
                    <td className="text-muted-foreground py-2 pr-4">{h.affectedScope || "—"}</td>
                    <td className="text-muted-foreground py-2">{h.createdByName ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Staff-admin deactivation (PE-991, Workflow B). Winds a tenant down while
// keeping its portal login + data: self-service write actions and API tokens
// are disabled server-side. Reversible via Reactivate. Node/jump-host teardown
// is manual and out of the portal's scope.
// Per-tenant MFA enforcement (the staged-rollout canary). Effective only when the
// environment master switch is also on, so staff can arm the customer's tenant
// after they've been notified without touching everyone at once.
function MfaPolicyCard({
  tenant,
  onChanged,
}: {
  tenant: AdminTenant;
  onChanged: (t: AdminTenant) => void;
}) {
  const required = tenant.mfaRequiredAt != null;
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const apply = async (next: boolean) => {
    setSubmitting(true);
    try {
      const updated = await tenantsAdminApi.setMfaRequired(tenant.id, next);
      onChanged(updated);
      toast.success(
        next
          ? `MFA is now required for ${tenant.name}. Members enroll on their next login.`
          : `MFA requirement removed for ${tenant.name}.`,
      );
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update the MFA requirement.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Multi-factor authentication</CardTitle>
        <CardDescription>
          Require this tenant's members to complete authenticator (TOTP) MFA at login. This is the
          per-tenant rollout control and only takes effect when MFA is enabled for the environment.
          Turning it on signs members out and prompts enrollment on their next login; turning it off
          lets them sign in without MFA again (their enrolled authenticators are kept).
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
          {required ? (
            <span className="text-muted-foreground">
              Required since {formatDateTime(tenant.mfaRequiredAt!)}.
            </span>
          ) : (
            <span className="text-muted-foreground">Not required for this tenant.</span>
          )}
        </div>
        {required ? (
          <Button
            variant="outline"
            className="shrink-0"
            onClick={() => void apply(false)}
            disabled={submitting}
          >
            {submitting ? "Updating…" : "Remove requirement"}
          </Button>
        ) : (
          <Button
            variant="outline"
            className="shrink-0"
            onClick={() => setOpen(true)}
            disabled={submitting}
          >
            Require MFA
          </Button>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => (submitting ? undefined : setOpen(o))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Require MFA for {tenant.name}?</DialogTitle>
            <DialogDescription>
              Every member of this tenant will be signed out and must set up an authenticator app
              the next time they log in. Make sure they have been notified first. You can remove the
              requirement later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={() => void apply(true)} disabled={submitting}>
              {submitting ? "Enabling…" : "Require MFA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function LifecycleCard({
  tenant,
  onChanged,
}: {
  tenant: AdminTenant;
  onChanged: (t: AdminTenant) => void;
}) {
  const deactivated = tenant.deactivatedAt != null;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const onDeactivate = async () => {
    setSubmitting(true);
    try {
      const updated = await tenantsAdminApi.deactivate(tenant.id, reason.trim() || undefined);
      onChanged(updated);
      toast.success(
        `Deactivated ${tenant.name}. Portal access is retained; self-service actions are disabled.`,
      );
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to deactivate the tenant.");
    } finally {
      setSubmitting(false);
    }
  };

  const onReactivate = async () => {
    setSubmitting(true);
    try {
      const updated = await tenantsAdminApi.reactivate(tenant.id);
      onChanged(updated);
      toast.success(`Reactivated ${tenant.name}.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to reactivate the tenant.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lifecycle</CardTitle>
        <CardDescription>
          Deactivate a tenant whose contract has ended or whose POC is over. They keep portal login
          and all their data, but self-service actions (SSH keys, network access, invites, API
          tokens) are disabled and existing API tokens stop working. Deallocate GPU nodes and jump
          hosts separately.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
          {deactivated ? (
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground">
                Deactivated {formatDateTime(tenant.deactivatedAt!)}.
              </span>
              {tenant.deactivationReason ? (
                <span className="text-muted-foreground">Reason: {tenant.deactivationReason}</span>
              ) : null}
            </div>
          ) : (
            <span className="text-muted-foreground">This tenant is active.</span>
          )}
        </div>
        {deactivated ? (
          <Button
            variant="outline"
            className="shrink-0"
            onClick={() => void onReactivate()}
            disabled={submitting}
          >
            {submitting ? "Reactivating…" : "Reactivate"}
          </Button>
        ) : (
          <Button
            variant="outline"
            className="shrink-0"
            onClick={() => setOpen(true)}
            disabled={submitting}
          >
            Deactivate
          </Button>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => (submitting ? undefined : setOpen(o))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate {tenant.name}?</DialogTitle>
            <DialogDescription>
              They keep portal login and read access, but can't create SSH keys, request
              network/ports, invite members, or mint API tokens, and existing API tokens stop
              working. You can reactivate later.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="deactivate-reason">Reason (optional)</Label>
            <Input
              id="deactivate-reason"
              placeholder="Contract ended"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={() => void onDeactivate()} disabled={submitting}>
              {submitting ? "Deactivating…" : "Deactivate tenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Staff-admin hard delete (PE-991, Workflow A). Full, irreversible removal of a
// never-onboarded / test tenant across NetBox + the portal DB. Gated behind a
// type-the-slug confirmation; the server refuses if the tenant still has live
// GPU nodes. The Key Vault private key can't be deleted by the portal and is
// reported as retained (manual purge) on success.
function DangerZoneCard({ tenant }: { tenant: AdminTenant }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setConfirm("");
  }, [open]);

  const onConfirm = async () => {
    if (confirm !== tenant.slug) return;
    setSubmitting(true);
    try {
      const result = await tenantsAdminApi.hardDelete(tenant.id, confirm);
      toast.success(
        `Deleted ${tenant.name}.${
          result.keyVaultRetained
            ? " Its Key Vault private key is retained and needs a manual purge."
            : ""
        }`,
      );
      setOpen(false);
      navigate("/admin/tenants");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete the tenant.");
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="text-destructive">Danger zone</CardTitle>
        <CardDescription>
          Permanently delete this tenant. Use only for a test tenant or one that was never onboarded
          - for a real tenant whose contract has ended, deactivate it instead.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-muted-foreground text-sm">
          Removes the tenant, its members, keys, and settings from the portal and deletes the linked
          NetBox tenant (releasing its identifier and network numbering). This cannot be undone.
        </p>
        <Button
          variant="destructive"
          className="shrink-0"
          onClick={() => setOpen(true)}
          disabled={submitting}
        >
          Hard delete tenant
        </Button>
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => (submitting ? undefined : setOpen(o))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {tenant.name}?</DialogTitle>
            <DialogDescription>
              This permanently deletes the tenant and all its portal data, and deletes the linked
              NetBox tenant. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hard-delete-confirm">
              Type the slug <span className="text-foreground font-mono">{tenant.slug}</span> to
              confirm
            </Label>
            <Input
              id="hard-delete-confirm"
              className="font-mono"
              autoComplete="off"
              spellCheck={false}
              placeholder={tenant.slug}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void onConfirm()}
              disabled={submitting || confirm !== tenant.slug}
            >
              {submitting ? "Deleting…" : "Delete tenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function StaffTenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const isStaffAdmin = user?.kind === "staff" && user.staffRole === "admin";
  const [tenant, setTenant] = useState<AdminTenant | null>(null);
  const [members, setMembers] = useState<AdminTenantMember[] | null>(null);
  const [invites, setInvites] = useState<AdminPendingInvite[] | null>(null);
  const [keys, setKeys] = useState<AdminTenantSshKey[] | null>(null);
  const [allowedIps, setAllowedIps] = useState<AdminTenantAllowedIp[] | null>(null);
  const [holds, setHolds] = useState<Hold[] | null>(null);
  const [error, setError] = useState(false);
  const [membersError, setMembersError] = useState(false);
  const [invitesError, setInvitesError] = useState(false);
  const [keysError, setKeysError] = useState(false);
  const [allowedIpsError, setAllowedIpsError] = useState(false);
  const [holdsError, setHoldsError] = useState(false);

  // Tracks the tenant currently shown, so async refreshes can drop responses
  // that arrive after the user navigated to a different tenant.
  const currentIdRef = useRef<string | undefined>(id);

  // Re-fetch members + pending invites after sending an invite, so the page
  // reflects the new pending row (and any member that accepted in the meantime).
  const refreshRoster = (tenantId: string) => {
    void tenantsAdminApi
      .members(tenantId)
      .then((res) => {
        if (currentIdRef.current !== tenantId) return;
        setMembers(res.users);
        setMembersError(false);
      })
      .catch(() => {
        if (currentIdRef.current === tenantId) setMembersError(true);
      });
    void tenantsAdminApi
      .invites(tenantId)
      .then((res) => {
        if (currentIdRef.current !== tenantId) return;
        setInvites(res.invites);
        setInvitesError(false);
      })
      .catch((err) => {
        if (currentIdRef.current !== tenantId) return;
        setInvitesError(true);
        toast.error(err instanceof ApiError ? err.message : "Failed to load pending invites.");
      });
  };

  useEffect(() => {
    if (!id) return;
    currentIdRef.current = id;
    let active = true;
    setError(false);
    setMembersError(false);
    setInvitesError(false);
    setKeysError(false);
    setAllowedIpsError(false);
    setHoldsError(false);
    setTenant(null);
    setMembers(null);
    setInvites(null);
    setKeys(null);
    setAllowedIps(null);
    setHolds(null);
    (async () => {
      try {
        const t = await tenantsAdminApi.get(id);
        if (active) setTenant(t);
      } catch (err) {
        if (active) {
          setError(true);
          toast.error(err instanceof ApiError ? err.message : "Failed to load the tenant.");
        }
        return;
      }
      // Members and keys load independently; a failure in one shouldn't blank
      // the other. Each surfaces its own toast and an inline error (kept
      // distinct from a genuinely empty section).
      void tenantsAdminApi
        .members(id)
        .then((res) => active && setMembers(res.users))
        .catch((err) => {
          if (active) {
            setMembersError(true);
            toast.error(err instanceof ApiError ? err.message : "Failed to load members.");
          }
        });
      void tenantsAdminApi
        .invites(id)
        .then((res) => active && setInvites(res.invites))
        .catch((err) => {
          if (active) {
            setInvitesError(true);
            toast.error(err instanceof ApiError ? err.message : "Failed to load pending invites.");
          }
        });
      void tenantsAdminApi
        .sshKeys(id)
        .then((res) => active && setKeys(res.keys))
        .catch((err) => {
          if (active) {
            setKeysError(true);
            toast.error(err instanceof ApiError ? err.message : "Failed to load SSH keys.");
          }
        });
      void tenantsAdminApi
        .allowedIps(id)
        .then((res) => active && setAllowedIps(res.allowedIps))
        .catch((err) => {
          if (active) {
            setAllowedIpsError(true);
            toast.error(err instanceof ApiError ? err.message : "Failed to load allowed ranges.");
          }
        });
      void tenantsAdminApi
        .holds(id)
        .then((res) => active && setHolds(res.holds))
        .catch((err) => {
          if (active) {
            setHoldsError(true);
            toast.error(err instanceof ApiError ? err.message : "Failed to load holds.");
          }
        });
    })();
    return () => {
      active = false;
    };
  }, [id]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Button asChild variant="ghost" size="sm" className="w-fit -ml-2">
          <Link to="/admin/tenants">
            <ArrowLeft className="size-4" />
            All tenants
          </Link>
        </Button>
        <PageHeader
          title={tenant?.name ?? "Tenant"}
          description={tenant ? `Slug: ${tenant.slug}` : undefined}
        >
          {tenant ? (
            <TenantStatusBadge status={tenant.status} deactivatedAt={tenant.deactivatedAt} />
          ) : null}
        </PageHeader>
      </div>

      {error ? (
        <Card>
          <CardContent className="text-muted-foreground py-12 text-center text-sm">
            Couldn't load this tenant. It may not exist, or you may not have access.
          </CardContent>
        </Card>
      ) : (
        <>
          <MembersCard
            members={members}
            error={membersError}
            canResetMfa={isStaffAdmin}
            mfaRequired={tenant?.mfaRequiredAt != null}
            onReset={async (member) => {
              try {
                await tenantsAdminApi.resetMemberMfa(member.id);
                setMembers(
                  (current) =>
                    current?.map((item) =>
                      item.id === member.id ? { ...item, mfaEnabled: false } : item,
                    ) ?? null,
                );
                toast.success(`MFA reset for ${member.email}.`);
              } catch (err) {
                toast.error(err instanceof ApiError ? err.message : "Unable to reset MFA.");
                throw err;
              }
            }}
          />
          {isStaffAdmin && id ? (
            <InviteCard tenantId={id} onCreated={() => refreshRoster(id)} />
          ) : null}
          <PendingInvitesCard invites={invites} error={invitesError} />
          {id ? (
            <FeaturesCard key={`${id}-features`} tenantId={id} isStaffAdmin={isStaffAdmin} />
          ) : null}
          {id ? <NodesCard key={id} tenantId={id} isStaffAdmin={isStaffAdmin} /> : null}
          <HoldsCard holds={holds} error={holdsError} />
          <SshKeysCard keys={keys} error={keysError} />
          <AllowedIpsCard entries={allowedIps} error={allowedIpsError} />
          {isStaffAdmin && tenant ? (
            <MfaPolicyCard
              tenant={tenant}
              onChanged={(updated) => {
                if (currentIdRef.current === updated.id) setTenant(updated);
              }}
            />
          ) : null}
          {isStaffAdmin && tenant ? (
            <LifecycleCard
              tenant={tenant}
              onChanged={(updated) => {
                // Drop a lifecycle response that resolved after staff navigated
                // to a different tenant, so it can't overwrite the shown one.
                if (currentIdRef.current === updated.id) setTenant(updated);
              }}
            />
          ) : null}
          {isStaffAdmin && tenant ? <DangerZoneCard tenant={tenant} /> : null}
        </>
      )}
    </div>
  );
}
